import { useState, useCallback } from "react";
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs";
import { StackLayout } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { FieldWrapper, Label } from "@progress/kendo-react-all";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { TextArea, TextAreaChangeEvent } from "@progress/kendo-react-inputs";
import { DataGrid, DataGridColumnProps } from "@excent-one/react/data-grid";
import { trashIcon, plusIcon, pencilIcon } from "@progress/kendo-svg-icons";
import { notification } from "@excent-one/react/notification";
import {
  createConcernsIssue,
  updateConcernsIssue,
  deleteConcernsIssue,
} from "@phrmpc/infrastructure/phrmpc/api/concerns-issues";
import {
  type ConcernsIssueDto,
  type ConcernsIssueTypeDto,
  type ConcernStatusDto,
} from "@phrmpc/infrastructure/phrmpc/models";
import { useQueryClient } from "@tanstack/react-query";

type ConcernRow = ConcernsIssueDto & { _rowKey: string };

interface ConcernsManagerWindowProps {
  concerns: ConcernsIssueDto[];
  concernIssueOptions: ConcernsIssueTypeDto[];
  concernStatusOptions: ConcernStatusDto[];
  readOnly?: boolean;
  dailyActivityMonitoringId: string;
  memberId: string;
  memberName: string;
  onClose: () => void;
  onSubmit: (concerns: ConcernsIssueDto[]) => void;
}

const stripRowKey = ({ _rowKey, ...dto }: ConcernRow): ConcernsIssueDto => dto;

const ConcernsManagerWindow = ({
  concerns,
  concernIssueOptions,
  concernStatusOptions,
  readOnly,
  dailyActivityMonitoringId,
  memberId,
  memberName,
  onClose,
  onSubmit,
}: ConcernsManagerWindowProps) => {
  const [pending, setPending] = useState<ConcernRow[]>(() =>
    concerns.map((c) => ({ ...c, _rowKey: c.id ?? crypto.randomUUID() })),
  );

  const queryClient = useQueryClient();

  const makeEmptyDraft = useCallback(
    (): ConcernRow => ({
      _rowKey: "",
      dailyActivityMonitoringId,
      memberId,
      memberName,
      concernsIssueTypeId: undefined,
      concernsIssueTypeName: undefined,
      description: "",
      statusId: undefined,
      statusName: undefined,
      concernDate: undefined,
    }),
    [dailyActivityMonitoringId, memberId, memberName],
  );

  const [draft, setDraft] = useState<ConcernRow>(makeEmptyDraft);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);

  const selectedIssueOption =
    concernIssueOptions.find((o) => o.id === draft.concernsIssueTypeId) ?? null;
  const selectedStatusOption =
    concernStatusOptions.find((o) => o.id === draft.statusId) ?? null;

  const handleAddOrUpdateDraft = useCallback(() => {
    if (!draft.concernsIssueTypeId) return;

    if (editingIndex !== null) {
      setPending((prev) =>
        prev.map((item, i) =>
          i === editingIndex ? { ...draft, _rowKey: item._rowKey } : item,
        ),
      );
      setEditingIndex(null);
    } else {
      setPending((prev) => [
        ...prev,
        { ...draft, _rowKey: crypto.randomUUID() },
      ]);
    }
    setDraft(makeEmptyDraft());
  }, [draft, editingIndex, makeEmptyDraft]);

  const handleEdit = useCallback(
    (index: number) => {
      setDraft(pending[index]);
      setEditingIndex(index);
    },
    [pending],
  );

  const handleCancelEdit = useCallback(() => {
    setDraft(makeEmptyDraft());
    setEditingIndex(null);
  }, [makeEmptyDraft]);

  const handleRemove = useCallback(
    async (index: number) => {
      const item = pending[index];

      if (item.id) {
        setRemovingIndex(index);
        try {
          await deleteConcernsIssue(item.id);
          await queryClient.invalidateQueries({
            queryKey: ["daml-concerns", dailyActivityMonitoringId],
          });
        } catch (e) {
          console.error(e);
          notification.error("Unable to remove concern. Please try again.");
          setRemovingIndex(null);
          return;
        }
        setRemovingIndex(null);
      }

      setPending((prev) => prev.filter((_, i) => i !== index));
      if (editingIndex === index) {
        handleCancelEdit();
      }
    },
    [pending, editingIndex, handleCancelEdit],
  );

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    try {
      const resolved = await Promise.all(
        pending.map(async (row) => {
          const dto = stripRowKey(row);

          if (dto.id) {
            const updated = await updateConcernsIssue(dto.id, {
              id: dto.id,
              payload: dto,
            });
            return { ...row, ...updated };
          }

          const created = await createConcernsIssue({ payload: dto });
          return { ...row, ...created, _rowKey: created.id ?? row._rowKey };
        }),
      );
      await queryClient.invalidateQueries({
        queryKey: ["daml-concerns", dailyActivityMonitoringId],
      });
      onSubmit(resolved.map(stripRowKey));
      onClose();
    } catch (e) {
      console.error(e);
      notification.error("Unable to save concerns. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [pending, onSubmit, onClose]);

  const columns: DataGridColumnProps<ConcernRow>[] = [
    { field: "concernsIssueTypeName", title: "Concern/Issue" },
    { field: "description", title: "Description" },
    { field: "statusName", title: "Status", width: "140px" },
  ];

  return (
    <Window
      title="Manage Concerns"
      onClose={onClose}
      initialWidth={650}
      initialHeight={560}
    >
      <StackLayout orientation="vertical" gap={16}>
        {!readOnly && (
          <StackLayout orientation="vertical" gap={8}>
            <FieldWrapper>
              <Label>Concerns/Issue*</Label>
              <DropDownList
                data={concernIssueOptions}
                dataItemKey="id"
                textField="name"
                value={selectedIssueOption}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    concernsIssueTypeId: e.value?.id,
                    concernsIssueTypeName: e.value?.name,
                  }))
                }
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>Description</Label>
              <TextArea
                rows={2}
                value={draft.description}
                onChange={(e: TextAreaChangeEvent) =>
                  setDraft((d) => ({ ...d, description: String(e.value) }))
                }
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key.length > 1) return;
                  if (!/^[a-zA-Z0-9\s]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e: React.ClipboardEvent<HTMLTextAreaElement>) => {
                  const pasted = e.clipboardData.getData("text");
                  if (!/^[a-zA-Z0-9\s]*$/.test(pasted)) {
                    e.preventDefault();
                  }
                }}
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>Concern/Issue Status</Label>
              <DropDownList
                data={concernStatusOptions}
                dataItemKey="id"
                textField="name"
                value={selectedStatusOption}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    statusId: e.value?.id,
                    statusName: e.value?.name,
                  }))
                }
              />
            </FieldWrapper>
            <StackLayout orientation="horizontal" gap={8}>
              <Button
                type="button"
                svgIcon={plusIcon}
                themeColor="base"
                disabled={!draft.concernsIssueTypeId}
                onClick={handleAddOrUpdateDraft}
              >
                {editingIndex !== null ? "Update" : "Add to list"}
              </Button>
              {editingIndex !== null && (
                <Button type="button" onClick={handleCancelEdit}>
                  Cancel Edit
                </Button>
              )}
            </StackLayout>
          </StackLayout>
        )}

        {pending.length > 0 && (
          <DataGrid
            readOnly={readOnly}
            hideRowCommandToggle={false}
            dataItemKey="_rowKey"
            data={pending}
            canAdd={false}
            columns={columns}
            commands={
              readOnly
                ? []
                : [
                    { command: "edit", text: "Edit", svgIcon: pencilIcon },
                    { command: "remove", text: "Remove", svgIcon: trashIcon },
                  ]
            }
            onCommandClicked={{
              edit: (e) => {
                const dataItem = e.dataItem;
                if (!dataItem) return;
                const index = pending.findIndex(
                  (p) => p._rowKey === dataItem._rowKey,
                );
                if (index > -1) handleEdit(index);
              },
              remove: (e) => {
                const dataItem = e.dataItem;
                if (!dataItem) return;
                const index = pending.findIndex(
                  (p) => p._rowKey === dataItem._rowKey,
                );
                if (index > -1) handleRemove(index);
              },
            }}
          />
        )}
      </StackLayout>

      <WindowActionsBar>
        <Button type="button" onClick={onClose}>
          Cancel
        </Button>
        {!readOnly && (
          <Button
            type="button"
            themeColor="primary"
            onClick={handleSubmit}
            disabled={saving || removingIndex !== null}
          >
            {saving ? "Saving..." : "Save Concerns"}
          </Button>
        )}
      </WindowActionsBar>
    </Window>
  );
};

export default ConcernsManagerWindow;
